import * as api from "@/apiv2/globalSearch";
import * as productApi from "@/apiv2/products";
import { STATUS } from "@/utils/constants";

import { handleError } from "../utils/error";

import {
  INIT_INSTANCE,
  REMOVE_INSTANCE,
  RESET,
  SET_IS_FETCHING_STATUS,
  SET_IS_SEARCH_RESULT_OPEN,
  SET_PRODUCTS,
  SET_SEARCH,
} from "./add-product-to-kit-inline.actionTypes";
import * as selectors from "./add-product-to-kit-inline.selectors";

export const initInstance = (instanceId) => (dispatch) => {
  dispatch({
    type: INIT_INSTANCE,
    payload: { instanceId },
  });
};

export const removeInstance = (instanceId) => (dispatch) => {
  dispatch({
    type: REMOVE_INSTANCE,
    payload: { instanceId },
  });
};

export const setIsFetchingStatus = (instanceId, status) => (dispatch) => {
  dispatch({
    type: SET_IS_FETCHING_STATUS,
    payload: { instanceId, status },
  });
};

export const setProducts = (instanceId, products) => (dispatch) => {
  dispatch({
    type: SET_PRODUCTS,
    payload: { instanceId, products },
  });
};

export const setIsSearchResultOpen = (instanceId, isOpen) => (dispatch) => {
  dispatch({
    type: SET_IS_SEARCH_RESULT_OPEN,
    payload: { instanceId, isOpen },
  });
};

export const setSearch = (instanceId, search) => (dispatch) => {
  dispatch({
    type: SET_SEARCH,
    payload: { instanceId, search },
  });
};

export const reset = (instanceId) => (dispatch) => {
  dispatch({
    type: RESET,
    payload: { instanceId },
  });
};

export const searchProducts =
  ({
    instanceId,
    q,
    pageSize,
    isSoldByCompany = false,
    isPurchasedByCompany = false,
    supplierUid,
    includeKits = true,
  }) =>
  async (dispatch, getState) => {
    const state = getState();
    const search = selectors.getSearch(state, instanceId);
    dispatch(setIsFetchingStatus(instanceId, STATUS.PENDING));

    try {
      const { data } = await api.getGlobalSearchProductsForOrder(
        {
          q,
          page_size: pageSize,
          is_sold_by_company: `${isSoldByCompany}`,
          is_purchased_by_company: `${isPurchasedByCompany}`,
          supplier_uid: supplierUid,
        }
      )();

      if (data.results.length > 0) {
        const filterNonVariantProducts = (d) => {
          const isNotVariant = d.index !== "factory_product_variant";
          const isNotKit = includeKits || d.index !== "factory_product_kit";
          return isNotVariant && isNotKit;
        };

        const productsMatchInKeyword = data.results.filter(filterNonVariantProducts).filter((d) => {
          // Use full_name for factory_user, name for other types
          const nameField = d.index === "factory_user" ? d.source.fullName : d.source.name;
          return nameField?.toLowerCase().includes(search.toLowerCase());
        });

        const productsNotMatchInKeyword = data.results
          .filter(filterNonVariantProducts)
          .filter((d) => {
            // Use full_name for factory_user, name for other types
            const nameField = d.index === "factory_user" ? d.source.fullName : d.source.name;
            return nameField && !nameField.toLowerCase().includes(search.toLowerCase());
          });

        const productVariants = data.results.filter((d) => d.index === "factory_product_variant");
        const products = data.results.filter(filterNonVariantProducts);

        if (productsMatchInKeyword.length > 0) {
          dispatch(
            setProducts(instanceId, [
              ...productsMatchInKeyword,
              ...productVariants,
              ...productsNotMatchInKeyword,
            ])
          );

          return [...productsMatchInKeyword, ...productVariants, ...productsNotMatchInKeyword];
        }

        dispatch(setProducts(instanceId, [...productVariants, ...products]));
        return [...productVariants, ...products];
      }

      dispatch(setProducts(instanceId, []));
      return [];
    } catch (error) {
      handleError(error, dispatch);
    } finally {
      dispatch(setIsFetchingStatus(instanceId, STATUS.FULFILLED));
    }
  };

export const fetchProductVariantDetails =
  ({ productUid, productVariantUid }) =>
  async () => {
    const { data } = await productApi.getProductVariantDetails({
      productUid,
      productVariantUid,
    })();
    return data;
  };

export const closeAllSearchResults = () => (dispatch, getState) => {
  const state = getState();
  const instanceIds = selectors.getAllInstanceIds(state);

  instanceIds.forEach((instanceId) => {
    dispatch(setIsSearchResultOpen(instanceId, false));
  });
};
